import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import { admiOwnerService } from './admi-owner.service';
import { Owner } from './crm.dto';

/**
 * Resolves a HubSpot owner-id to its full Owner via `/v2/crm/owners/{id}`,
 * re-fetching whenever [id] changes. Used to hydrate Account-Manager
 * dropdowns from a URL deep-link without leaking a raw id into the UI.
 *
 * Returns a `[owner, setOwner]` tuple so any number of callers can drive
 * the value — e.g. the URL drives it via the [id] argument while the
 * Autocomplete drives it via the setter when the user picks a different
 * owner. Both paths converge on the same state.
 *
 * Race-safety:
 * - the resolved id is recorded in a ref *before* the async call begins,
 *   so clearing the owner mid-flight (state → null while the id is still
 *   the same, e.g. user clicks X) does not trigger a re-fetch.
 * - the ref also gates against stale responses: a hydration started for
 *   an older id is dropped once the id has moved on.
 *
 * When HubSpot 404s the id (deleted/unknown owner) the hook seeds a
 * placeholder Owner whose [displayName] is the id itself — keeps the
 * filter visible without hiding that we couldn't resolve a name.
 */
export function useHydratedOwner(id: string | null): [Owner | null, Dispatch<SetStateAction<Owner | null>>] {
    const [owner, setOwner] = useState<Owner | null>(null);
    const hydratedIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (!id) {
            hydratedIdRef.current = null;
            return;
        }
        if (hydratedIdRef.current === id) return;
        if (owner?.id === id) {
            hydratedIdRef.current = id;
            return;
        }
        // Claim the id up front: even if the network call hasn't returned,
        // a subsequent `setOwner(null)` (user clears the input) won't
        // re-enter this branch — the ref still matches the URL id.
        hydratedIdRef.current = id;
        let cancelled = false;
        (async () => {
            try {
                const fetched = await admiOwnerService.getOwnerById(id);
                if (cancelled || hydratedIdRef.current !== id) return;
                setOwner(fetched ?? placeholderOwner(id));
            } catch (err) {
                if (cancelled) return;
                console.error('useHydratedOwner: failed to fetch owner', err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [id, owner]);

    return [owner, setOwner];
}

function placeholderOwner(id: string): Owner {
    return { id, email: null, firstName: null, lastName: null, displayName: id };
}
